import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "../../context/TranslationContext";
import { isValidPhoneNumber } from "react-phone-number-input";
import { API_URL } from "../../config/env";
import { rememberAuthRedirect } from "../../utils/authRedirect";
import InstagramVerificationModal from "../InstagramVerificationModal";
import SocialMediaLinkingModal from "../SocialMediaLinkingModal";
import Button from "../ds/Button";
import PasswordField from "../ds/PasswordField";
import PhoneField from "../ds/PhoneField";
import SegmentedControl from "../ds/SegmentedControl";
import TextField from "../ds/TextField";
import { ArrowRightIcon, FacebookLogo, GoogleLogo } from "../ds/icons";

/*
  The Join | Sign in switch and both forms from Figma A2 · შესვლა და რეგისტრაცია.
  Used on the /auth page and in the sign-in popup. `onAuthenticated` runs once
  the visitor is in: right after signing in, or after the social-link step
  that follows a new account. `redirectTo` is where a Google or Facebook
  sign-in returns to.
*/

const passwordRule = /^[A-Z].{7,}$/; // first char uppercase + min length 8
const emailRule = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL_SIGN_UP = {
  username: "",
  email: "",
  password: "",
  confirm: "",
  platforms: {
    Instagram: "",
    TikTok: "",
    YouTube: "",
    Facebook: "",
  },
  /** Full international number from phone input (E.164), e.g. +995577123456 */
  phoneE164: "",
};

const OrDivider = () => (
  <div className="flex items-center gap-space-12">
    <span className="h-px flex-1 bg-separator-default" />
    <span className="text-caps-label uppercase text-label-tertiary">OR</span>
    <span className="h-px flex-1 bg-separator-default" />
  </div>
);

const SocialButtons = ({ redirectTo }) => {
  const continueWith = (provider) => {
    rememberAuthRedirect(redirectTo);
    window.location.href = `${API_URL}/auth/${provider}`;
  };
  return (
    <>
      <Button variant="gray" fullWidth icon={<GoogleLogo />} onClick={() => continueWith("google")}>
        Continue with Google
      </Button>
      <Button variant="gray" fullWidth icon={<FacebookLogo />} onClick={() => continueWith("facebook")}>
        Continue with Facebook
      </Button>
    </>
  );
};

const AuthForms = ({ isDark, setToast, activeForm, onFormChange, onAuthenticated, redirectTo = null }) => {
  const { t } = useTranslation();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showInstagramVerificationModal, setShowInstagramVerificationModal] = useState(false);
  const [showSocialMediaLinking, setShowSocialMediaLinking] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [openingInstagramVerification, setOpeningInstagramVerification] = useState(false);
  const [signUp, setSignUp] = useState(INITIAL_SIGN_UP);
  const [signIn, setSignIn] = useState({
    identifier: "",
    password: "",
  });
  const [errors, setErrors] = useState({});

  const validateSignUp = () => {
    const nextErrors = {};
    if (!signUp.username.trim()) nextErrors.username = "Username is required";
    if (!emailRule.test(signUp.email)) nextErrors.email = "Use a valid email";
    if (!passwordRule.test(signUp.password))
      nextErrors.password = "Password must start with uppercase, min 8 chars";
    if (signUp.password !== signUp.confirm)
      nextErrors.confirm = "Passwords don't match";
    if (!signUp.phoneE164 || !isValidPhoneNumber(signUp.phoneE164)) {
      nextErrors.phoneNumber = t("auth.phoneInvalid");
    }
    return nextErrors;
  };

  const switchForm = (form) => {
    onFormChange(form);
    setErrors({});
    setToast("");
  };

  const handleSignUpSubmit = async (event) => {
    event.preventDefault();
    const finalErrors = validateSignUp();
    if (Object.keys(finalErrors).length > 0) {
      setErrors(finalErrors);
      return;
    }
    setErrors({});
    setLoading(true);
    const userData = {
      username: signUp.username,
      email: signUp.email,
      password: signUp.password,
      accountType: "creator",
      role: "creator",
      phone: signUp.phoneE164,
    };
    const result = await register(userData);
    setLoading(false);
    if (result.success) {
      setToast(t("toasts.accountCreated"));
      setAccountCreated(true);

      // Show social media linking after account creation
      // Don't navigate away - show modal instead
      // Wait a bit longer for auth context to update and ensure modal shows
      setTimeout(() => {
        setShowSocialMediaLinking(true);
      }, 1000); // Increased delay to ensure auth context is updated

      // Reset signUp state after a delay to allow modal to show
      setTimeout(() => {
        setSignUp(INITIAL_SIGN_UP);
      }, 2000);
    } else {
      setToast(result.message || t("toasts.registrationFailed"));
    }
  };

  const handleSignInSubmit = async (event) => {
    event.preventDefault();
    if (!signIn.identifier.trim()) {
      setErrors({ identifier: "Username or email is required" });
      return;
    }
    if (!signIn.password.trim()) {
      setErrors({ signInPassword: "Password is required" });
      return;
    }
    setErrors({});
    setLoading(true);
    const result = await login(signIn.identifier, signIn.password);
    setLoading(false);
    if (result.success) {
      setToast(t("toasts.signedInSuccessfully"));
      setSignIn({ identifier: "", password: "" });
      onAuthenticated();
    } else {
      setToast(result.message || t("toasts.loginFailedGeneric"));
    }
  };

  const clearError = (field) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSignUpChange = (field) => (event) => {
    const value = event.target.value;
    setSignUp((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    clearError(field);
  };

  const handlePlatformLinkChange = (platform, value) => {
    setSignUp((prev) => ({
      ...prev,
      platforms: {
        ...prev.platforms,
        [platform]: value,
      },
    }));
    // Automatically open Instagram verification modal when Instagram link is added
    if (platform === "Instagram" && value && value.trim() !== "") {
      setTimeout(() => {
        setShowInstagramVerificationModal(true);
      }, 300);
    }
  };

  const handleSignInChange = (field, errorKey) => (event) => {
    setSignIn((prev) => ({ ...prev, [field]: event.target.value }));
    clearError(errorKey);
  };

  return (
    <div className="flex flex-col gap-space-16">
      <SegmentedControl
        label="Join or sign in"
        value={activeForm}
        onChange={switchForm}
        options={[
          { value: "signup", label: "Join Mintly" },
          { value: "signin", label: "Sign in" },
        ]}
      />

      {activeForm === "signup" ? (
        <form className="flex flex-col gap-space-16" onSubmit={handleSignUpSubmit} noValidate>
          <TextField
            label="Username"
            placeholder="Username"
            value={signUp.username}
            onChange={handleSignUpChange("username")}
            autoComplete="username"
            error={errors.username}
          />
          <TextField
            label="Email"
            type="email"
            placeholder="Email"
            value={signUp.email}
            onChange={handleSignUpChange("email")}
            autoComplete="email"
            error={errors.email}
          />
          <PhoneField
            label="Phone Number"
            placeholder="Phone Number"
            value={signUp.phoneE164}
            onChange={(val) => {
              setSignUp((prev) => ({ ...prev, phoneE164: val || "" }));
              clearError("phoneNumber");
            }}
            error={errors.phoneNumber}
          />
          <PasswordField
            label="Password"
            placeholder="Password"
            value={signUp.password}
            onChange={handleSignUpChange("password")}
            autoComplete="new-password"
            rule="Password: start with an uppercase, minimum 8 characters."
            ruleMet={passwordRule.test(signUp.password)}
            ruleError={Boolean(errors.password)}
          />
          <PasswordField
            label="Confirm Password"
            placeholder="Confirm Password"
            value={signUp.confirm}
            onChange={handleSignUpChange("confirm")}
            autoComplete="new-password"
            error={errors.confirm}
          />
          <Button type="submit" fullWidth loading={loading} icon={<ArrowRightIcon />}>
            {loading ? "Creating account..." : "Create Mintly account"}
          </Button>
          <OrDivider />
          <SocialButtons redirectTo={redirectTo} />
        </form>
      ) : (
        <form className="flex flex-col gap-space-16" onSubmit={handleSignInSubmit} noValidate>
          <TextField
            label="Username or email"
            placeholder="Username or email"
            value={signIn.identifier}
            onChange={handleSignInChange("identifier", "identifier")}
            autoComplete="username"
            error={errors.identifier}
          />
          <PasswordField
            label="Password"
            placeholder="Password"
            value={signIn.password}
            onChange={handleSignInChange("password", "signInPassword")}
            autoComplete="current-password"
            error={errors.signInPassword}
          />
          <p className="text-footnote text-label-secondary">
            Sign back into Mintly to sync chats, collabs, and drops.
          </p>
          <Button type="submit" fullWidth loading={loading} icon={<ArrowRightIcon />}>
            {loading ? "Signing in..." : "Enter Mintly"}
          </Button>
          <OrDivider />
          <SocialButtons redirectTo={redirectTo} />
        </form>
      )}

      {/* Social Media Linking Modal - Shows after account creation */}
      <SocialMediaLinkingModal
        isOpen={showSocialMediaLinking}
        setIsOpen={(open) => {
          setShowSocialMediaLinking(open);
          // Only move on if closing AND not opening Instagram verification AND Instagram modal is not open
          if (!open && accountCreated && !openingInstagramVerification && !showInstagramVerificationModal) {
            setTimeout(onAuthenticated, 500);
          }
        }}
        setToast={setToast}
        isDark={isDark}
        onComplete={() => {
          // User skipped
          setAccountCreated(false);
          setShowSocialMediaLinking(false);
          onAuthenticated();
        }}
        onInstagramVerificationRequest={() => {
          // Set flag to prevent moving on BEFORE closing modal
          setOpeningInstagramVerification(true);
          // Open Instagram verification modal immediately
          setShowInstagramVerificationModal(true);
          // Close social media linking modal after a short delay
          setTimeout(() => {
            setShowSocialMediaLinking(false);
          }, 100);
        }}
      />

      {/* Instagram Verification Modal */}
      <InstagramVerificationModal
        isOpen={showInstagramVerificationModal}
        setIsOpen={(open) => {
          setShowInstagramVerificationModal(open);
          // Reset flag when closing
          if (!open) {
            setOpeningInstagramVerification(false);
            // Move on after closing modal only if account was created
            if (accountCreated) {
              setTimeout(onAuthenticated, 300);
            }
          }
        }}
        setToast={setToast}
        isDark={isDark}
        initialUsername=""
        onInstagramLinkUpdate={(url) => {
          // Update Instagram link in signup form
          handlePlatformLinkChange("Instagram", url);
        }}
      />
    </div>
  );
};

export default AuthForms;
